import { AxiosPromise } from 'axios';
import { get } from 'lodash';

import { reviewInstance } from './axios';

import { Pagination } from '@app/types/common';
import { Review, ReviewCreation } from '@app/types/review';
import { getAuthorizationHeader } from '@app/utils/authorization';

type CreateReviewOptions = {
  sourceExpand?: boolean;
  targetExpand?: boolean;
};

/**
 * Create user review use review-service
 * @param token
 * @param fingerprint
 * @param userId
 * @param payload
 * @param options
 * @returns
 */
export function createUserReview(
  token: string,
  fingerprint: string,
  userId: string,
  payload: ReviewCreation,
  options?: CreateReviewOptions
): AxiosPromise<Review> {
  const urlParams = [];
  const expandParams: Array<string> = [];

  // set $expand
  if (options?.sourceExpand) {
    expandParams.push('source');
  }
  if (options?.targetExpand) {
    expandParams.push('target');
  }
  if (expandParams.length > 0) {
    urlParams.push(['$expand', expandParams.join(',')]);
  }

  const query =
    urlParams.length > 0 ? `?${new URLSearchParams(urlParams).toString()}` : '';
  const url = `/users/${userId}/reviews-by-org${query}`;
  return reviewInstance
    .post(url, payload, {
      headers: {
        'x-nb-fingerprint': fingerprint,
        ...getAuthorizationHeader(token),
      },
    })
    .catch((error) => {
      if ('message' in error.response.data) {
        throw new Error(error.response?.data.message);
      } else {
        throw new Error(error.message);
      }
    });
}

/**
 * Get user review list of a user use review-service
 * @param token
 * @param fingerprint
 * @param options
 * @returns
 */
export function getUserReviews(
  token: string,
  fingerprint: string,
  userId: string,
  options?: {
    $expand?: string;
    '@nextLink'?: string;
    limit?: number;
    orderBy?: string | 'createdAt desc';
  }
): AxiosPromise<Pagination<Review[]>> {
  const urlParams = [['$top', String(options?.limit || 20)]];
  const filterParams: Array<string> = [];
  if (options?.['@nextLink']) {
    const nextToken = new URLSearchParams(
      get(options?.['@nextLink'].split('?'), '[1]', '')
    ).get('$nextToken');
    if (nextToken) {
      urlParams.push(['$nextToken', nextToken]);
    }
  }

  if (options?.orderBy) {
    urlParams.push(['$orderBy', options.orderBy]);
  }
  if (options?.$expand) {
    urlParams.push(['$expand', options.$expand]);
  }
  if (filterParams.length > 0) {
    urlParams.push(['$filter', filterParams.join(' and ')]);
  }
  return reviewInstance
    .get(
      `/users/${userId}/reviews-by-org?${new URLSearchParams(
        urlParams
      ).toString()}`,
      {
        headers: {
          'x-nb-fingerprint': fingerprint,
          ...getAuthorizationHeader(token),
        },
      }
    )
    .catch(
      (error: {
        message: string | undefined;
        response: { data: { message: string | undefined } };
      }) => {
        if (error.response && 'message' in error.response.data) {
          throw new Error(error.response?.data.message);
        } else {
          throw new Error(error.message);
        }
      }
    );
}
